import React, { useEffect, useState, useCallback } from 'react';
import './OutdatedVersionBanner.css';

/**
 * Always-visible escape hatch that lets users self-update when their
 * installed PWA or cached tab is running an old JS bundle.
 *
 * Polls /api/version every 2 minutes and compares against the version
 * recorded on boot. If the server has advanced AND we haven't already
 * auto-reloaded, surface a bar at the top inviting the user to update.
 *
 * Why an explicit banner (on top of bootVersionCheck which already
 * auto-reloads on drift)? Because some platforms — iOS Safari PWAs,
 * Brave shields, aggressive corporate proxies — prevent the automatic
 * Service Worker update flow from landing the new bundle. This banner
 * gives users a single tap escape-hatch that is guaranteed to work:
 * unregister SW, purge caches, hard-reload.
 */

const VERSION_KEY = 'ckk_app_version';
const DISMISSED_KEY = 'ckk_outdated_banner_dismissed_for';
const POLL_MS = 2 * 60 * 1000;

const getBackendBase = () => {
  const host = window.location.hostname;
  if (host === 'ckkcriseofthedukes.online' || host === 'www.ckkcriseofthedukes.online') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const OutdatedVersionBanner = () => {
  const [visible, setVisible] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    const base = getBackendBase();
    if (!base) return;
    try {
      const r = await fetch(`${base}/api/version?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) return;
      const { version } = await r.json();
      if (!version) return;
      let boot = null;
      try { boot = localStorage.getItem(VERSION_KEY); } catch { /* ignore */ }
      if (!boot || boot === version) {
        setVisible(false);
        return;
      }
      // Version drifted — check if user recently dismissed this exact server version
      let dismissedFor = null;
      try { dismissedFor = localStorage.getItem(DISMISSED_KEY); } catch { /* ignore */ }
      if (dismissedFor === version) {
        setVisible(false);
        return;
      }
      setServerVersion(version);
      setVisible(true);
    } catch {
      // Network blip — keep current state, check again next poll
    }
  }, []);

  useEffect(() => {
    check();
    const iv = setInterval(check, POLL_MS);
    const onVisibility = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check]);

  const handleUpdate = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    // Hide the banner immediately so the user sees a clean screen while the
    // SW unregister + cache purge + reload happens. After reload, the new
    // bundle's boot version matches the server version (we set it below)
    // so the banner stays hidden.
    setVisible(false);
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister().catch(() => {})));
      }
    } catch { /* ignore */ }
    try {
      if (typeof caches !== 'undefined') {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n).catch(() => {})));
      }
    } catch { /* ignore */ }
    try {
      if (serverVersion) localStorage.setItem(VERSION_KEY, serverVersion);
      localStorage.removeItem('ckk_reloaded_after_version_mismatch');
      sessionStorage.removeItem('ckk_reloaded_after_version_mismatch');
    } catch { /* ignore */ }
    window.location.reload();
  }, [busy, serverVersion]);

  const handleDismiss = useCallback(() => {
    try { if (serverVersion) localStorage.setItem(DISMISSED_KEY, serverVersion); } catch { /* ignore */ }
    setVisible(false);
  }, [serverVersion]);

  if (!visible) return null;

  return (
    <div className="outdated-banner" role="alert" data-testid="outdated-version-banner">
      <span className="outdated-banner-text">
        A new version is available. Update now for the latest fixes.
      </span>
      <div className="outdated-banner-actions">
        <button
          type="button"
          className="outdated-banner-btn outdated-banner-btn-primary"
          onClick={handleUpdate}
          disabled={busy}
          data-testid="outdated-banner-update"
        >
          {busy ? 'Updating…' : 'Update'}
        </button>
        <button
          type="button"
          className="outdated-banner-btn outdated-banner-btn-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="outdated-banner-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default OutdatedVersionBanner;
